// Returning own-story Path. Eight spoken stops over the live prompt packs.
// Does not add questions. A tap opens one room (the stop's primary pack) with
// that pack's first prompt as a silent opener. Hot Seat is not a stop on this home.
import { PROMPT_PACKS } from '../constants/promptPacks';
import { resolvablePages } from './bookHelpers';

// Card titles are the spoken labels. Catalog names stay on the packs themselves.
// Hot Seat is not on this trail.
// A tap never asks which pack. Multi-pack primaries (the other pack still lights
// the stop when a saved chapter names it):
//   Where I grew up → easy-start
//   The ones I still laugh about → trouble-i-got-into
export const PATH_STOPS = [
  { id: 'grew-up', label: 'Where I grew up', packIds: ['easy-start', 'where-i-come-from'] },
  { id: 'takes-me-right-back', label: 'Takes me right back', packIds: ['takes-me-right-back'] },
  { id: 'firsts', label: 'Firsts', packIds: ['firsts'] },
  { id: 'how-we-fell-in-love', label: 'How we fell in love', packIds: ['how-we-fell-in-love'] },
  { id: 'raising-the-kids', label: 'Raising the kids', packIds: ['becoming-a-family'] },
  { id: 'people-who-shaped-me', label: 'People who shaped me', packIds: ['people-who-marked-me'] },
  { id: 'work', label: 'Work and money', packIds: ['work-money-hustle'] },
  { id: 'laugh', label: 'The ones I still laugh about', packIds: ['trouble-i-got-into', 'total-disasters'] },
];

export const PATH_RETURN_KEY = 'tb_path_return';

// Explicit so a reorder of packIds cannot silently change which room opens.
const PATH_PRIMARY_PACK = {
  'grew-up': 'easy-start',
  laugh: 'trouble-i-got-into',
};

const PACK_BY_ID = new Map(PROMPT_PACKS.map(pack => [pack.id, pack]));
const PROMPT_TO_PACK = new Map();
PROMPT_PACKS.forEach(pack => {
  pack.prompts.forEach(prompt => {
    PROMPT_TO_PACK.set(prompt.id, pack.id);
    PROMPT_TO_PACK.set(`${pack.id}:${prompt.id}`, pack.id);
  });
});

export function attributePackId(story) {
  if (!story) return null;
  const promptId = typeof story.promptId === 'string' ? story.promptId.trim() : '';
  if (!promptId) return null;
  return PROMPT_TO_PACK.get(promptId) || null;
}

export function primaryPackId(stopOrId) {
  const id = typeof stopOrId === 'string' ? stopOrId : (stopOrId && stopOrId.id);
  if (!id) return null;
  if (PATH_PRIMARY_PACK[id]) return PATH_PRIMARY_PACK[id];
  const stop = PATH_STOPS.find(item => item.id === id);
  return stop && stop.packIds[0] ? stop.packIds[0] : null;
}

// Same shape StRecordPage builds for a tapped card: id packId:promptId, emoji, text self.
// The opener stays in this pack, and the save keeps promptId. Not a new greeting.
export function silentOpenerForPack(packId) {
  if (!packId || !stopIdForPack(packId)) return null;
  const pack = PACK_BY_ID.get(packId);
  const prompt = pack && Array.isArray(pack.prompts) ? pack.prompts[0] : null;
  if (!prompt || typeof prompt.self !== 'string' || !prompt.self.trim()) return null;
  return {
    id: `${pack.id}:${prompt.id}`,
    emoji: pack.emoji,
    text: prompt.self,
  };
}

// Voice when the device can do WebRTC. Otherwise the typed interview uses the same opener.
export function isPathVoiceCapable(nav, win) {
  const n = nav === undefined && typeof navigator !== 'undefined' ? navigator : nav;
  const w = win === undefined && typeof window !== 'undefined' ? window : win;
  return !!(n && n.mediaDevices && typeof n.mediaDevices.getUserMedia === 'function')
    && !!(w && (w.RTCPeerConnection || w.webkitRTCPeerConnection || w.mozRTCPeerConnection));
}

export function resolvePathRoomMount({ packId, selectedPrompt, voiceCapable }) {
  const text = selectedPrompt && typeof selectedPrompt.text === 'string' ? selectedPrompt.text.trim() : '';
  const prompt = text ? selectedPrompt : silentOpenerForPack(packId);
  if (!prompt) return { prompt: null, enterVoice: false, autoStart: false, enterText: false };
  if (voiceCapable) return { prompt, enterVoice: true, autoStart: true, enterText: false };
  return { prompt, enterVoice: false, autoStart: false, enterText: true };
}

export function stopIdForPack(packId) {
  if (!packId) return null;
  const stop = PATH_STOPS.find(item => item.packIds.includes(packId));
  return stop ? stop.id : null;
}

export function pathStopViews() {
  return PATH_STOPS.map(stop => ({
    id: stop.id,
    label: stop.label,
    packs: stop.packIds.map(id => {
      const pack = PACK_BY_ID.get(id);
      return { id, name: pack.name, blurb: pack.blurb };
    }),
  }));
}

// storiesLoaded false, or a story list that never arrived: count chapters, do not
// invent which stop they belong to.
export function buildPathProgress({ pages, stories, storiesLoaded }) {
  const list = Array.isArray(stories) ? stories : [];
  const chapterPages = resolvablePages(pages, list);
  const progress = {
    chapterCount: chapterPages.length,
    unattributed: 0,
    offTrail: 0,
    stopCounts: {},
    packCounts: {},
    attributionReady: false,
  };
  if (!storiesLoaded || list.length === 0) return progress;
  const byId = new Map(list.map(story => [story.id, story]));
  chapterPages.forEach(page => {
    const story = byId.get(page.storyId);
    const packId = story ? attributePackId(story) : null;
    const stopId = stopIdForPack(packId);
    if (!packId) {
      progress.unattributed += 1;
      return;
    }
    progress.packCounts[packId] = (progress.packCounts[packId] || 0) + 1;
    if (!stopId) {
      progress.offTrail += 1;
      return;
    }
    progress.stopCounts[stopId] = (progress.stopCounts[stopId] || 0) + 1;
  });
  progress.attributionReady = true;
  return progress;
}

export function shouldShowReturningPath({ isGiftBook, userDataLoaded, chapterCount }) {
  if (!userDataLoaded) return false;
  if (isGiftBook) return false;
  return (chapterCount || 0) >= 1;
}

export function chaptersSavedLine(count) {
  const n = count || 0;
  return n === 1 ? '1 chapter saved' : `${n} chapters saved`;
}

export function stopStatusLine(count) {
  if (!count) return '';
  return count === 1 ? '1 chapter saved here' : `${count} chapters saved here`;
}

export function unattributedLine(chapterCount, unattributed) {
  if (!unattributed) return '';
  if (unattributed === chapterCount) {
    return chapterCount === 1
      ? 'It did not come from a topic, so no stop is marked.'
      : 'They did not come from a topic, so no stop is marked.';
  }
  return unattributed === 1
    ? '1 saved chapter did not come from a topic.'
    : `${unattributed} saved chapters did not come from a topic.`;
}

// Same handshake StTalkHome "Start talking" already uses.
export function applySeedlessStart(api) {
  if (!api) return;
  if (api.setStStartMode) api.setStStartMode('voice');
  if (api.setActivePackId) api.setActivePackId('easy-start');
  if (api.setStSelectedPrompt) api.setStSelectedPrompt(null);
  if (api.setStInterviewMode) api.setStInterviewMode(false);
  if (api.setForcePackPicker) api.setForcePackPicker(false);
  if (api.setSeedlessStart) api.setSeedlessStart(true);
  if (api.setPathOpenPackId) api.setPathOpenPackId(null);
  api.setActiveTab('st-record');
}

// Path tap. One pack, that pack's first prompt as a silent opener, no chooser.
// Just start talking stays applySeedlessStart (null prompt, seedlessStart, cold opener).
export function openPathRoom(api, packId) {
  if (!api) return null;
  const prompt = silentOpenerForPack(packId);
  if (!prompt) return null;
  if (api.setSeedlessStart) api.setSeedlessStart(false);
  if (api.setForcePackPicker) api.setForcePackPicker(false);
  if (api.setStStartMode) api.setStStartMode('voice');
  if (api.setActivePackId) api.setActivePackId(packId);
  if (api.setStSelectedPrompt) api.setStSelectedPrompt(prompt);
  if (api.setStInterviewMode) api.setStInterviewMode(false);
  try { sessionStorage.setItem(PATH_RETURN_KEY, '1'); } catch (e) { /* private mode */ }
  if (api.setPathOpenPackId) api.setPathOpenPackId(packId);
  if (api.setActiveTab) api.setActiveTab('st-record');
  return { packId, prompt };
}

// Hot Seat still opens rapid-fire. Every other pack opens the Path room.
export function openPathPack(api, packId) {
  if (!api) return;
  if (packId === 'hot-seat') {
    if (api.setSeedlessStart) api.setSeedlessStart(false);
    if (api.setForcePackPicker) api.setForcePackPicker(false);
    if (api.setStSelectedPrompt) api.setStSelectedPrompt(null);
    if (api.setActivePackId) api.setActivePackId(packId);
    if (api.setRapidAnswers) api.setRapidAnswers({});
    if (api.setRapidCurrentIdx) api.setRapidCurrentIdx(0);
    if (api.setStInterviewMode) api.setStInterviewMode(true);
    if (api.setStInterviewStep) api.setStInterviewStep('rapid-fire');
    if (api.logInterviewStart) api.logInterviewStart();
    try { sessionStorage.setItem(PATH_RETURN_KEY, '1'); } catch (e) { /* private mode */ }
    if (api.setPathOpenPackId) api.setPathOpenPackId(packId);
    if (api.setActiveTab) api.setActiveTab('st-record');
    return;
  }
  openPathRoom(api, packId);
}
